import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { memo, useEffect, useState } from "react";
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Dimensions,
  Image,
  Linking,
  ScrollView,
} from "react-native";
import { ReadOnlyProps } from "../../../utils/templates";
import { BlurView } from "expo-blur";
import { IconButton, Text } from "react-native-paper";
import Spinner from "../../../components/shared/Spinner";
import { Ionicons } from "@expo/vector-icons";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import * as Print from "expo-print";
import { Platform } from "react-native";
import { validSignedURLWithModule } from "../../../utils/validateSignedUrl";
import mapiConfig from "../../../configs/mapi";
import { useToast } from "react-native-toast-notifications";
import { useTranslation } from "../../../utils/useTranslation";

interface DownloadPdfDeliveryContentProps {
  selectedItem: any;
  setModalVisible: (visible: boolean) => void;
}

const DownloadPdfDeliveryContent: React.FC<DownloadPdfDeliveryContentProps> =
  memo(({ selectedItem, setModalVisible }) => {
    const { colors } = useAppTheme();
    const styles = createStyles(colors);
    const toast = useToast();
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const [printing, setPrinting] = useState(false);
    const [docNum] = useState(selectedItem?.DocNum);
    const [officialDnUrl, setOfficialDnUrl] = useState<string | null>(null);

    useEffect(() => {
      let isMounted = true;

      if (isMounted && docNum) {
        setLoading(true);
        validSignedURLWithModule(
          "DN" + docNum,
          "label",
          "delivery-note",
          () => {},
          (url: string | null) => {
            if (isMounted) {
              setOfficialDnUrl(url || null);
              setLoading(false);
            }
          },
          "pdf/*",
        );
      }

      return () => {
        isMounted = false;
      };
    }, [docNum]);

    const isOfficialPdfValid =
      officialDnUrl && !officialDnUrl.includes("no-image.png");

    const printPDF = async () => {
      if (!isOfficialPdfValid) return;

      try {
        setPrinting(true);

        if (Platform.OS === "web") {
          const response = await fetch(
            `${mapiConfig.api.domain}/s3/proxy/getImageFromS3`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                url: officialDnUrl as string,
                bucket: "multisteps-label",
              }),
            },
          );

          if (!response.ok) throw new Error(`Proxy failed: ${response.status}`);

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          const iframe = document.createElement("iframe");
          iframe.style.position = "fixed";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.style.width = "0";
          iframe.style.height = "0";
          iframe.style.border = "none";
          iframe.src = blobUrl;

          iframe.onload = () => {
            // PDF is loaded — stop the spinner and trigger print
            setPrinting(false);
            setTimeout(() => {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
            }, 500);
            setTimeout(() => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(blobUrl);
            }, 60_000);
          };

          iframe.onerror = () => {
            setPrinting(false);
            document.body.removeChild(iframe);
            URL.revokeObjectURL(blobUrl);
            toast.show(t("couldNotLoadDeliveryNote") || "Could not load the delivery note.", {
              type: "danger",
            });
          };

          document.body.appendChild(iframe);
        } else {
          const fileUri =
            FileSystem.documentDirectory + `DeliveryNote_${docNum}.pdf`;
          const downloadResult = await FileSystem.downloadAsync(
            officialDnUrl as string,
            fileUri,
          );
          await Print.printAsync({ uri: downloadResult.uri });
          setPrinting(false);
        }
      } catch (err) {
        console.error("Print PDF failed", err);
        setPrinting(false);
        toast.show(t("couldNotLoadDeliveryNote") || "Could not load the delivery note.", {
          type: "danger",
        });
      }
    };

    const viewPDF = async () => {
      try {
        if (isOfficialPdfValid) {
          if (Platform.OS === "web") {
            window.open(officialDnUrl as string, "_blank");
          } else {
            const fileUri =
              FileSystem.documentDirectory + `DeliveryNote_${docNum}.pdf`;
            const downloadResult = await FileSystem.downloadAsync(
              officialDnUrl as string,
              fileUri,
            );
            await Sharing.shareAsync(downloadResult.uri, {
              mimeType: "application/pdf",
              dialogTitle: "View PDF",
            });
          }
        }
      } catch (err) {
        console.error("View PDF failed", err);
      }
    };

    const downloadPDF = async () => {
      try {
        if (isOfficialPdfValid) {
          if (Platform.OS === "web") {
            try {
              const response = await fetch(officialDnUrl as string);
              const blob = await response.blob();
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `DeliveryNote_${docNum}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            } catch {
              window.open(officialDnUrl as string, "_blank");
            }
          } else {
            const fileUri =
              FileSystem.documentDirectory + `DeliveryNote_${docNum}.pdf`;
            const downloadResult = await FileSystem.downloadAsync(
              officialDnUrl as string,
              fileUri,
            );
            await Sharing.shareAsync(downloadResult.uri);
          }
        }
      } catch (err) {
        console.error("Download PDF failed", err);
      }
    };

    return (
      <View style={styles.container}>
        {loading ? (
          <View style={styles.loaderContainer}>
            <Spinner
              size="small"
              color={colors.primary}
              styles={{ alignSelf: "center" }}
            />
          </View>
        ) : (
          <>
            <ScrollView
              contentContainerStyle={styles.contentContainer}
              showsVerticalScrollIndicator={false}
              style={{ flex: 1 }}
            >
              {/* Header */}
              <View style={styles.header}>
                <Text style={styles.headerTitle}>{t("documentDownloader") || "Document Downloader"}</Text>
                <Text style={styles.headerSubtitle}>
                  {t("documentNo") || "Document No"}: #{docNum}
                </Text>
              </View>

              {/* Documents Section */}
              <Text style={styles.sectionTitle}>{t("documents") || "Documents"}</Text>
              <View style={styles.filesListContainer}>
                {/* System Generated / S3 PDF Card */}
                {isOfficialPdfValid && (
                  <View style={styles.previewCard}>
                    <View style={styles.previewContent}>
                      <View
                        style={[
                          styles.docIconContainer,
                          styles.pdfIconContainer,
                        ]}
                      >
                        <Ionicons
                          name="document-text"
                          size={32}
                          color={colors.danger}
                        />
                      </View>
                      <View style={styles.fileDetails}>
                        <Text
                          style={styles.fileName}
                          numberOfLines={1}
                          ellipsizeMode="middle"
                        >
                          DN{docNum}.pdf
                        </Text>
                      </View>
                    </View>
                    <View style={styles.cardActions}>
                      <TouchableOpacity
                        style={[styles.cardBtn, styles.pdfPrintBtn]}
                        onPress={printPDF}
                        disabled={printing}
                      >
                        {printing ? (
                          <Spinner
                            size="small"
                            color={colors.danger}
                            styles={{ marginRight: 6 }}
                          />
                        ) : (
                          <Ionicons
                            name="print-outline"
                            size={16}
                            color={colors.danger}
                            style={{ marginRight: 6 }}
                          />
                        )}
                        <Text style={styles.pdfPrintBtnText}>
                          {printing ? (t("printing") || "Printing...") : (t("printPdf") || "Print PDF")}
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.cardBtn,
                          styles.pdfViewBtn,
                          { marginRight: 0 },
                        ]}
                        onPress={viewPDF}
                      >
                        <Ionicons
                          name="eye-outline"
                          size={16}
                          color={colors.primary}
                          style={{ marginRight: 6 }}
                        />
                        <Text style={styles.pdfViewBtnText}>{t("viewPdf") || "View PDF"}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}

                {/* No Attachments */}
                {!isOfficialPdfValid && (
                  <View style={styles.noAttachmentsCard}>
                    <Ionicons
                      name="document-text-outline"
                      size={24}
                      color={colors.textSecondary}
                      style={{ marginRight: 8 }}
                    />
                    <Text style={styles.noAttachmentsText}>
                      {t("noDeliveryNote") || "No delivery note available"}
                    </Text>
                  </View>
                )}
              </View>
            </ScrollView>
          </>
        )}
      </View>
    );
  });

const ModalContainer: React.FC<ReadOnlyProps<Props>> = memo(
  ({ childern, modalVisible, setModalVisible }) => {
    const Children = childern;
    const { colors } = useAppTheme();
    const styles = createStyles(colors);

    return (
      <View>
        <Modal
          animationType="slide"
          transparent={true}
          visible={modalVisible}
          onRequestClose={() => setModalVisible(!modalVisible)}
        >
          <BlurView intensity={10} style={styles.centeredView}>
            <View
              style={[
                styles.modalView,
                {
                  width: "90%",
                  maxWidth: 680,
                  minHeight: 480,
                  maxHeight: "90%",
                },
              ]}
            >
              {modalVisible && Children && <Children />}
              <IconButton
                style={styles.button}
                icon="close"
                color={colors.textSecondary}
                size={22}
                onPress={() => setModalVisible(!modalVisible)}
              />
            </View>
          </BlurView>
        </Modal>
      </View>
    );
  },
);

interface DownloadPdfDeliveryModalProps {
  selectedItem: any;
  downloadPdfModalVisible: boolean;
  setDownloadPdfModalVisible: (visible: boolean) => void;
}

export const DownloadPdfDeliveryModal: React.FC<DownloadPdfDeliveryModalProps> =
  memo(
    ({ selectedItem, downloadPdfModalVisible, setDownloadPdfModalVisible }) => {
      return (
        <ModalContainer
          modalVisible={downloadPdfModalVisible}
          setModalVisible={setDownloadPdfModalVisible}
          childern={() => (
            <DownloadPdfDeliveryContent
              selectedItem={selectedItem}
              setModalVisible={setDownloadPdfModalVisible}
            />
          )}
        />
      );
    },
  );

interface Props {
  childern: React.ElementType;
  modalVisible?: boolean;
  isMobileDevice?: boolean;
  setModalVisible: (event: any) => void;
}

// Styles
const createStyles = (colors: any) => StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(15, 23, 42, 0.4)",
  },
  modalView: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    alignItems: "stretch",
    justifyContent: "flex-start",
    shadowColor: "#0F172A",
    shadowOffset: {
      width: 0,
      height: 10,
    },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 10,
    overflow: "hidden",
  },
  button: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: 999,
    backgroundColor: colors.surfaceSubtle,
    margin: 0,
  },
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: colors.surface,
    borderRadius: 16,
  },
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  contentContainer: {
    padding: 24,
    paddingTop: 32,
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.surfaceSubtle,
    paddingBottom: 16,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: "700",
    color: colors.text,
  },
  headerSubtitle: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: colors.text,
    marginBottom: 12,
  },
  filesListContainer: {
    marginBottom: 12,
  },
  previewCard: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  previewContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  thumbnail: {
    width: 48,
    height: 48,
    borderRadius: 6,
  },
  docIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 6,
    backgroundColor: colors.primaryBg,
    alignItems: "center",
    justifyContent: "center",
  },
  pdfIconContainer: {
    backgroundColor: colors.dangerBg,
  },
  chepDocIconContainer: {
    backgroundColor: colors.success + "20",
  },
  fileDetails: {
    flex: 1,
    marginLeft: 12,
  },
  fileName: {
    fontSize: 14,
    fontWeight: "600",
    color: colors.text,
  },
  fileType: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.textSecondary,
    marginTop: 2,
  },
  cardActions: {
    flexDirection: "row",
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.surfaceSubtle,
    paddingTop: 12,
  },
  cardBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    flex: 1,
  },
  viewBtn: {
    backgroundColor: colors.primaryBg,
    marginRight: 8,
  },
  viewBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.primary,
  },
  chepViewBtn: {
    backgroundColor: colors.success + "20",
    marginRight: 8,
  },
  chepViewBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.success,
  },
  pdfPrintBtn: {
    backgroundColor: colors.dangerBg,
    marginRight: 8,
  },
  pdfPrintBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.danger,
  },
  pdfViewBtn: {
    backgroundColor: colors.primaryBg,
    marginRight: 8,
  },
  pdfViewBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.primary,
  },
  pdfDownloadBtn: {
    backgroundColor: colors.success + "20",
  },
  pdfDownloadBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.success,
  },
  noAttachmentsCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 16,
    borderStyle: "dashed",
  },
  noAttachmentsText: {
    fontSize: 14,
    color: colors.textSecondary,
    fontWeight: "500",
  },
});
